// src/components/quizzes/PuzzleShuffle.js
//
// ===========================================================================
// THIS FILE WAS NOT A PUZZLE
// ===========================================================================
// BonusRouter has:
//
//     case "puzzleshuffle":
//       return <PuzzleShuffle questions placeholderImage onComplete />;
//
// and PuzzleShuffle.js contained a stale copy of MultipleChoice — same
// component name (`const MultipleChoice = ...`), same answer buttons, same
// `import "./MultipleChoice.css"`. So a teacher who picked "Puzzle Shuffle"
// as the bonus round got a second multiple-choice quiz, using the same
// questions the child had just answered.
//
// It also carried a live crash: `user.role.toLowerCase()` with no guard, on a
// prop that BonusRouter never passes. Any child who reached the Home button
// on that screen hit a TypeError.
//
// This is an actual sliding-tile puzzle.
//
// ===========================================================================
// DESIGN NOTES
// ===========================================================================
//
// SOLVABILITY. Half of all random arrangements of a sliding puzzle are
// mathematically unsolvable — the parity is wrong and no sequence of legal
// moves will ever finish it. Shuffling by `sort(() => Math.random() - 0.5)`
// gives a child a 50% chance of a puzzle they physically cannot complete.
// This shuffles by making N random LEGAL moves from the solved state, which
// is always solvable by construction.
//
// AN EXIT. Every other bonus round is on a timer, so a stuck child is
// released automatically. A puzzle isn't, so there's both a clock and an
// always-available "I'm finished" button. A seven-year-old who cannot solve a
// 4×4 must not be trapped in the bonus round with no way back to their score.
//
// PARTIAL CREDIT. Giving up or running out of time still scores per tile in
// its correct place. Zero for an unfinished puzzle would punish exactly the
// children who need the encouragement.
//
// IMAGE OR NUMBERS. Uses the topic's placeholderImage sliced by
// background-position when there is one, and falls back to big numbered tiles
// when there isn't — which is also the better mode for K-1 regardless.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import "./PuzzleShuffle.css";

const MUSIC_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fbonus-round1.mp3?alt=media&token=e4c606bb-90ca-4a78-9f2e-34a709084464";
const SLIDE_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Ftapsound1.mp3?alt=media&token=c60530e0-337c-4481-ae9c-87a64c076686";
const WIN_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306";
const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f";
const TALK_BTN_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Ftalktextbutton.png?alt=media&token=c3a19304-b98a-43cf-9d81-c9b80000375e";

const TUNING = {
  ROUND_SECONDS: 150,
  COMPLETION_BONUS: 100,
  PER_TILE: 10,           // partial credit for each tile left in place
  EFFICIENCY_POOL: 120,   // decays with moves used
  MOVE_COST: 2,
};

const makePool = (url, size = 4) => {
  const pool = Array.from({ length: size }, () => new Audio(url));
  let i = 0;
  return () => {
    const a = pool[i];
    i = (i + 1) % size;
    try {
      a.currentTime = 0;
      a.play().catch(() => {});
    } catch {}
  };
};

/** Solved board: [0,1,2,...,n-2, null] — null is the empty slot. */
const solvedBoard = (size) => {
  const total = size * size;
  return [...Array(total - 1).keys(), null];
};

const neighborsOf = (index, size) => {
  const row = Math.floor(index / size);
  const col = index % size;
  const out = [];
  if (row > 0) out.push(index - size);
  if (row < size - 1) out.push(index + size);
  if (col > 0) out.push(index - 1);
  if (col < size - 1) out.push(index + 1);
  return out;
};

/**
 * Shuffle by walking the empty slot around at random.
 *
 * Every state reachable this way is solvable, because every step is itself a
 * legal move that can simply be undone. Contrast with shuffling the array,
 * where half the results are unsolvable.
 */
const shuffleBoard = (size, steps = 160) => {
  const board = solvedBoard(size);
  let blank = board.indexOf(null);
  let previous = -1;

  for (let i = 0; i < steps; i++) {
    const options = neighborsOf(blank, size).filter((n) => n !== previous);
    const next = options[Math.floor(Math.random() * options.length)];
    board[blank] = board[next];
    board[next] = null;
    previous = blank;
    blank = next;
  }

  // A shuffle that lands back on solved would open the round already won.
  if (board.every((v, i) => v === solvedBoard(size)[i])) {
    return shuffleBoard(size, steps + 7);
  }
  return board;
};

const isSolved = (board) =>
  board.every((tile, i) => (i === board.length - 1 ? tile === null : tile === i));

export default function PuzzleShuffle({
  questions = [],
  placeholderImage,
  onComplete,
}) {
  // A topic can set { size: 4 } on its first question for a harder puzzle.
  const size = Math.min(4, Math.max(3, Number(questions?.[0]?.size) || 3));
  const total = size * size;

  const imageUrl = questions?.[0]?.puzzleImage || placeholderImage || "";
  const [imageOk, setImageOk] = useState(!!imageUrl);

  const [board, setBoard] = useState(() => shuffleBoard(size));
  const [moves, setMoves] = useState(0);
  const [timer, setTimer] = useState(TUNING.ROUND_SECONDS);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [wasSolved, setWasSolved] = useState(false);
  const [peeking, setPeeking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [lastMoved, setLastMoved] = useState(null);

  const musicRef = useRef(null);
  const playSlide = useRef(makePool(SLIDE_URL)).current;
  const playWin = useRef(makePool(WIN_URL, 2)).current;

  // Refs so the end-of-round handler reads live values, not a closure from
  // the render where the timer started. (This is the bug that makes
  // RhythmTapGame always submit a score of zero — see that file.)
  const stateRef = useRef({ board, moves, wasSolved: false });
  useEffect(() => {
    stateRef.current = { board, moves, wasSolved };
  }, [board, moves, wasSolved]);

  // ---------------- Audio ----------------
  useEffect(() => {
    musicRef.current = new Audio(MUSIC_URL);
    musicRef.current.loop = true;
    musicRef.current.volume = 0.28;
    musicRef.current.play().catch(() => {});
    return () => {
      musicRef.current?.pause();
      if (musicRef.current) musicRef.current.currentTime = 0;
    };
  }, []);

  // ---------------- Read aloud ----------------
  const canSpeak = typeof window !== "undefined" && "speechSynthesis" in window;
  const speak = (text) => {
    if (!canSpeak) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-US";
    u.rate = 0.92;
    window.speechSynthesis.speak(u);
  };

  const INTRO_SCRIPT = imageOk
    ? "Slide the tiles to put the picture back together. Tap any tile next to the empty space to slide it. Press Start when you are ready."
    : `Slide the tiles to put the numbers back in order, one through ${total - 1}. Tap any tile next to the empty space to slide it. Press Start when you are ready.`;

  // ---------------- Clock ----------------
  useEffect(() => {
    if (!started || finished) return;
    const t = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          setFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [started, finished]);

  // ---------------- Scoring & hand-off ----------------
  const finalScore = useMemo(() => {
    const { board: b, moves: m, wasSolved: solved } = stateRef.current;
    const placed = b.reduce(
      (n, tile, i) => n + (tile !== null && tile === i ? 1 : 0),
      0
    );
    const partial = placed * TUNING.PER_TILE;
    if (!solved) return partial;
    const efficiency = Math.max(0, TUNING.EFFICIENCY_POOL - m * TUNING.MOVE_COST);
    return partial + TUNING.COMPLETION_BONUS + efficiency;
    // Recomputed on finish only; the deps are intentionally the finish flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  useEffect(() => {
    if (!finished) return;
    if (finalScore > 0) {
      confetti({ spread: 100, particleCount: 130, startVelocity: 48, origin: { y: 0.5 } });
    }
    const t = setTimeout(() => onComplete?.(finalScore), 3000);
    return () => clearTimeout(t);
  }, [finished, finalScore, onComplete]);

  // ---------------- Moving ----------------
  const slide = useCallback(
    (index) => {
      if (!started || finished) return;

      setBoard((prev) => {
        const blank = prev.indexOf(null);
        if (!neighborsOf(blank, size).includes(index)) return prev;

        const next = [...prev];
        next[blank] = next[index];
        next[index] = null;

        playSlide();
        setLastMoved(blank);
        setMoves((m) => m + 1);

        if (isSolved(next)) {
          playWin();
          setWasSolved(true);
          // Let the final tile land before the summary covers the board.
          setTimeout(() => setFinished(true), 650);
        }

        return next;
      });
    },
    [started, finished, size, playSlide, playWin]
  );

  // Arrow keys move the tile that would slide INTO the gap in that direction,
  // which is the convention every sliding puzzle uses and the one that feels
  // right on a smartboard with a wireless keyboard.
  useEffect(() => {
    const onKey = (e) => {
      const blank = board.indexOf(null);
      const row = Math.floor(blank / size);
      const col = blank % size;
      let target = null;
      if (e.key === "ArrowUp" && row < size - 1) target = blank + size;
      if (e.key === "ArrowDown" && row > 0) target = blank - size;
      if (e.key === "ArrowLeft" && col < size - 1) target = blank + 1;
      if (e.key === "ArrowRight" && col > 0) target = blank - 1;
      if (target !== null) {
        e.preventDefault();
        slide(target);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [board, size, slide]);

  const placedCount = board.reduce(
    (n, tile, i) => n + (tile !== null && tile === i ? 1 : 0),
    0
  );
  const stars = wasSolved ? (moves <= total * 4 ? 3 : moves <= total * 8 ? 2 : 1) : 0;

  /** background-position for a tile, so one image is sliced into `total` cells. */
  const tileBackground = (tile) => {
    if (!imageOk || tile === null) return {};
    const row = Math.floor(tile / size);
    const col = tile % size;
    return {
      backgroundImage: `url(${imageUrl})`,
      backgroundSize: `${size * 100}% ${size * 100}%`,
      backgroundPosition: `${(col / (size - 1)) * 100}% ${(row / (size - 1)) * 100}%`,
    };
  };

  return (
    <div className="psz-root">
      {/* Preload so a dead Storage URL falls back to numbers rather than
          rendering nine blank squares the child can't tell apart. */}
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          style={{ display: "none" }}
          onError={() => setImageOk(false)}
        />
      )}

      <img src={LOGO_URL} alt="SBL Logo" className="psz-logo" />

      <div className="psz-hud">
        <div className="psz-card">
          <span className="psz-label">Moves</span>
          <div className="psz-value">{moves}</div>
        </div>
        <div className="psz-card">
          <span className="psz-label">In Place</span>
          <div className="psz-value">
            {placedCount}<span className="psz-of">/{total - 1}</span>
          </div>
        </div>
        <div className="psz-card">
          <span className="psz-label">Time</span>
          <div className={`psz-value ${timer <= 15 && started ? "urgent" : ""}`}>{timer}s</div>
        </div>
      </div>

      <div
        className={`psz-board ${peeking ? "peeking" : ""}`}
        style={{ gridTemplateColumns: `repeat(${size}, 1fr)` }}
      >
        {board.map((tile, index) => {
          if (tile === null) {
            return <div key={`blank-${index}`} className="psz-gap" aria-hidden="true" />;
          }

          const blank = board.indexOf(null);
          const canMove = started && !finished && neighborsOf(blank, size).includes(index);

          return (
            <button
              key={tile}
              type="button"
              className={`psz-tile ${canMove ? "movable" : ""} ${
                tile === index ? "home" : ""
              } ${lastMoved === index ? "just-moved" : ""} ${imageOk ? "img" : "num"}`}
              style={tileBackground(tile)}
              onClick={() => slide(index)}
              disabled={!canMove}
              aria-label={`Tile ${tile + 1}${canMove ? ", tap to slide" : ""}`}
            >
              {/* Numbers stay visible in image mode too — small, in the corner.
                  Without them a child who can't yet read the picture has no
                  way to reason about where a tile belongs. */}
              <span className={imageOk ? "psz-num-small" : "psz-num"}>{tile + 1}</span>
            </button>
          );
        })}

        {/* Peek overlay: the finished picture, held while the button is down. */}
        {peeking && imageOk && (
          <div
            className="psz-peek"
            style={{ backgroundImage: `url(${imageUrl})` }}
            aria-hidden="true"
          />
        )}
      </div>

      <div className="psz-controls">
        {imageOk && (
          <button
            type="button"
            className="psz-btn ghost"
            onPointerDown={() => setPeeking(true)}
            onPointerUp={() => setPeeking(false)}
            onPointerLeave={() => setPeeking(false)}
            disabled={!started || finished}
          >
            👁 Hold to Peek
          </button>
        )}

        <button
          type="button"
          className="psz-btn ghost"
          onClick={() => {
            setBoard(shuffleBoard(size));
            setMoves(0);
            setLastMoved(null);
          }}
          disabled={!started || finished}
        >
          🔀 Shuffle Again
        </button>

        {/* The escape hatch. Always available once the round starts. */}
        <button
          type="button"
          className="psz-btn"
          onClick={() => setFinished(true)}
          disabled={!started || finished}
        >
          ✓ I'm Finished
        </button>

        <button
          type="button"
          className="psz-mute"
          onClick={() => {
            const next = !isMuted;
            setIsMuted(next);
            if (musicRef.current) musicRef.current.muted = next;
          }}
          aria-label={isMuted ? "Turn music on" : "Turn music off"}
        >
          {isMuted ? "🔇" : "🔊"}
        </button>
      </div>

      <div className="psz-footer">© Smart Board Learning LLC. All rights reserved.</div>

      {/* ---------------- Intro ---------------- */}
      {!started && (
        <div className="psz-overlay">
          <div className="psz-panel">
            <div className="psz-title">🧩 Puzzle Shuffle</div>
            <div className="psz-sub">
              {imageOk
                ? "Slide the tiles to put the picture back together."
                : `Slide the tiles to put the numbers in order, 1 to ${total - 1}.`}
            </div>
            <p className="psz-hint">
              Tap any tile next to the empty square and it slides in. Arrow keys work too.
            </p>

            <div className="psz-actions">
              <button
                className="psz-talk"
                onClick={() => speak(INTRO_SCRIPT)}
                disabled={!canSpeak}
                aria-label="Read the directions out loud"
              >
                <img
                  src={TALK_BTN_URL}
                  alt="Talk text"
                  style={{ width: 110, height: 110, objectFit: "contain", display: "block" }}
                />
              </button>
              <button
                className="psz-start"
                onClick={() => {
                  if (canSpeak) window.speechSynthesis.cancel();
                  setStarted(true);
                }}
              >
                Start!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Summary ---------------- */}
      {finished && (
        <div className="psz-overlay">
          <div className="psz-panel">
            <div className="psz-title">
              {wasSolved ? "🎉 Puzzle Solved!" : "Nice try!"}
            </div>

            <div className="psz-stars" aria-label={`${stars} out of 3 stars`}>
              {[0, 1, 2].map((i) => (
                <span key={i} className={`st ${i < stars ? "" : "dim"}`}>⭐</span>
              ))}
            </div>

            <div className="psz-score-label">Bonus Points</div>
            <div className="psz-score">{finalScore}</div>

            <div className="psz-stats">
              <div className="psz-stat">
                <div className="n">{moves}</div>
                <div className="k">Moves</div>
              </div>
              <div className="psz-stat">
                <div className="n">{placedCount}</div>
                <div className="k">Tiles Placed</div>
              </div>
            </div>

            {!wasSolved && (
              <p className="psz-hint">
                You still earned points for every tile in the right place.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
